import React from 'react';
import { CheckIcon } from 'lucide-react';
import { PageHero } from '../../components/ui/PageHero';
import { Section } from '../../components/ui/Section';
import { TimetableTable } from '../../components/ui/TimetableTable';
import { TestimonialList } from '../../components/ui/TestimonialList';
import { CourseFaq } from '../../components/course/CourseFaq';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { CourseTabs } from '../../components/course/CourseTabs';
import { CourseGlance } from '../../components/course/CourseGlance';
import { CourseVideo } from '../../components/course/CourseVideo';
import { WeekTimeline } from '../../components/course/WeekTimeline';
import { CampusCompare } from '../../components/course/CampusCompare';
import { generalEnglishPage } from '../../data/courses/generalEnglishPage';
import { destinations } from '../../data/destinations';
import { testimonials } from '../../data/people';
import { sharedCourseFaqs } from '../../data/schedule';
import type { Course, Destination } from '../../types/content';
const tabs = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'video',
  label: 'Video'
}, {
  id: 'week',
  label: 'Your week'
}, {
  id: 'campuses',
  label: 'Campuses & prices'
}, {
  id: 'reviews',
  label: 'Reviews'
}, {
  id: 'faq',
  label: 'FAQ'
}];

// Shared course layout: sticky page tabs, a sticky "at a glance" card, a video slot and campus comparison.
export function CoursePage({
  course,
  groupLabel
}: {
  course: Course;
  groupLabel: string;
}) {
  const isGeneral = course.slug === 'general-english';
  const isYoung = course.group === 'young';
  const weeklyPricing = course.group === 'english';
  const showTimetable = course.group === 'english' && course.season === 'year-round' && course.slug !== 'es-flex';
  const details = isGeneral ? generalEnglishPage.details : [course.lead, course.valueText];
  const video = isGeneral ? generalEnglishPage.video : {
    url: '',
    poster: course.image,
    title: `Inside ${course.name}`,
    caption: `Meet our team and see what ${course.name} looks like at ES World.`
  };
  const campuses = destinations.filter(destination => course.locations.includes(destination.slug)).map(destination => ({
    destination,
    highlight: campusHighlight(destination, course, isGeneral)
  }));
  return <>
      <PageHero eyebrow={groupLabel} title={course.name} lead={course.lead} primary={{
      label: course.ctas.heroPrimary,
      to: '#enquire'
    }} secondary={{
      label: course.ctas.heroSecondary,
      to: '#week'
    }} image={course.image} imageAlt={`Students in a ${course.name} class`} data-id="element-1257" data-mp="32nobaeuoz3vu" />

      <CourseTabs items={tabs} cta={{
      label: course.ctas.heroPrimary,
      to: '#enquire'
    }} data-id="element-1258" data-mp="3qdsbec8o8li3" />

      <section id="overview" aria-labelledby="overview-title" className="w-full bg-white" data-id="element-1259" data-mp="3mcf3lwm0q88j">
        <div className="mx-auto grid max-w-content gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-12 lg:gap-16" data-id="element-1260" data-mp="2ey3lbc04f6d2">
          <div className="lg:col-span-7" data-id="element-1261" data-mp="2axjqif30seu3">
            <h2 id="overview-title" data-id="element-1262" data-mp="qdxwy3tkqisb">
              <span className="block type-h2-light text-ink" data-id="element-1263" data-mp="qezgyxskifm6">Course</span>
              <span className="block type-h2 text-brand" data-id="element-1264" data-mp="ufjdq098bh7s">details</span>
            </h2>
            {details.map(paragraph => <p key={paragraph.slice(0, 24)} className="mt-5 type-body text-ink" data-id="element-1265" data-mp="zbqbv6vdvof6">
                {paragraph}
              </p>)}

            {isGeneral ? <>
                <h3 className="mt-12 type-h4 text-ink" data-id="element-1266" data-mp="193cw1spp8xu2">Build all four skills</h3>
                <dl className="mt-6 grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-1267" data-mp="1e1efwfuy916l">
                  {generalEnglishPage.skills.map(skill => <div key={skill.name} className="border-t-2 border-brand pt-4" data-id="element-1268" data-mp="1n3f8gzf8mz1x">
                      <dt className="type-card-title text-ink" data-id="element-1269" data-mp="1n1b4khhca01n">{skill.name}</dt>
                      <dd className="mt-1 type-card-desc text-body" data-id="element-1270" data-mp="3jx8nfrb5b5q7">{skill.text}</dd>
                    </div>)}
                </dl>
              </> : <>
                <h3 className="mt-12 type-h4 text-ink" data-id="element-1271" data-mp="2tir4vplg4qk3">{course.valueTitle}</h3>
                <ul className="mt-6 grid gap-x-10 gap-y-5 sm:grid-cols-2" data-id="element-1272" data-mp="2okplt4o0sr8m">
                  {course.outcomes.map(outcome => <li key={outcome} className="border-t-2 border-brand pt-4 type-card-desc text-ink" data-id="element-1273" data-mp="33gp2h3p73zpd">
                      {outcome}
                    </li>)}
                </ul>
              </>}

            <h3 className="mt-12 type-h4 text-ink" data-id="element-1274" data-mp="25v9lahcc4ys7">{course.whoTitle}</h3>
            {!isGeneral ? <p className="mt-3 type-body text-body" data-id="element-1275" data-mp="2kjxdqeqvwsdx">{course.whoText}</p> : null}
            <ul className="mt-5 grid gap-x-8 sm:grid-cols-2" data-id="element-1276" data-mp="2km0dhqhu9zvu">
              {course.whoFor.map(item => <li key={item.title} className="flex gap-3 border-b border-line py-3" data-id="element-1277" data-mp="2frckbebw4hax">
                  <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1278" data-mp="1s1i7gwrkmsqo" />
                  <span className="type-list text-ink" data-id="element-1279" data-mp="1n2fggg8fen1p">{item.title}</span>
                </li>)}
            </ul>
          </div>
          <div className="lg:col-span-5" data-id="element-1280" data-mp="7dpn3sedeko4">
            <CourseGlance facts={course.facts} intensities={isGeneral ? generalEnglishPage.intensities : undefined} primary={{
            label: course.ctas.midPrimary,
            to: '#enquire'
          }} data-id="element-1281" data-mp="7esee1flup4k" />
          </div>
        </div>
      </section>

      <section id="video" aria-labelledby="video-title" className="w-full bg-white px-2 py-2 md:px-4" data-id="element-1282" data-mp="le0eqxni83ac">
        <div className="rounded-[28px] bg-cream md:rounded-[40px]" data-id="element-1283" data-mp="qaqt9gjnuqtn">
          <div className="mx-auto max-w-content px-5 py-16 md:px-8 md:py-24" data-id="element-1284" data-mp="cctucdhqi8bt">
            <h2 id="video-title" className="mb-10 md:mb-12" data-id="element-1285" data-mp="gdvv4yrausfw">
              <span className="block type-h2-light text-ink" data-id="element-1286" data-mp="gcb25dkmvhk5">See the course</span>
              <span className="block type-h2 text-brand" data-id="element-1287" data-mp="2x1gfybxn7flf">in action</span>
            </h2>
            <CourseVideo {...video} data-id="element-1288" data-mp="314d40i5yc3wr" />
          </div>
        </div>
      </section>

      <Section id="week" title={course.weekTitle} description={course.weekText} mockup="Sample week" data-id="element-1289" data-mp="17lxfekytppjj">
        <WeekTimeline week={course.week} data-id="element-1290" data-mp="12lrkmvx9bmx8" />
        {showTimetable ? <div className="mt-16" data-id="element-1291" data-mp="12onjcmgxifmq">
            <h3 className="mb-6 type-h4 text-ink" data-id="element-1292" data-mp="xut1zposqcqt">Pick a timetable</h3>
            <TimetableTable data-id="element-1293" data-mp="swhpt8b13q5f" />
          </div> : null}
      </Section>

      <Section id="campuses" bg="grey" title={campuses.length > 1 ? 'Choose your campus' : 'Where you study'} description={campuses.length > 1 ? `${course.name} runs at ${campuses.length} locations, with the same teaching method and student support. Compare what each city offers.` : `${course.name} runs at our ${campuses[0]?.destination.name ?? ''} campus.`} mockup={weeklyPricing ? undefined : 'Sample prices'} data-id="element-1294" data-mp="suf4h1ktml71">
        <CampusCompare campuses={campuses} season={course.season} weeklyPricing={weeklyPricing} priceListOverride={isYoung ? 'camps' : undefined} data-id="element-1295" data-mp="298dn04npomtz" />
      </Section>

      <Section id="reviews" title="What students tell us" mockup="Sample testimonials" data-id="element-1296" data-mp="2gu91lqvmyoy3">
        <TestimonialList items={isYoung ? [testimonials[3], testimonials[0], testimonials[4]] : testimonials.slice(0, 3)} data-id="element-1297" data-mp="2grwd0uvdhgc2" />
      </Section>

      <CourseFaq intro={`Everything students usually ask before joining ${course.name}.`} groups={[{
      id: 'course',
      label: 'About the course',
      items: course.faqs
    }, {
      id: 'booking',
      label: 'Booking & visa',
      items: sharedCourseFaqs
    }]} data-id="element-1298" data-mp="2qkbvdtal06xh" />

      <EnquirySection title={`Plan your ${course.name}`} description="Tell us your dates, your goal and where you want to study. Our admissions team replies with a plan, a price and the next steps." submitLabel={course.ctas.bottomPrimary} defaultCourse={course.slug} data-id="element-1299" data-mp="22tyacmqez8ce" />
    </>;
}
function campusHighlight(destination: Destination, course: Course, isGeneral: boolean) {
  if (isGeneral) {
    const custom = generalEnglishPage.campuses.find(item => item.slug === destination.slug);
    if (custom) return custom.highlight;
  }
  const lead = course.season === 'seasonal' ? destination.seasonal?.lead : destination.yearRound?.lead;
  const firstSentence = lead?.split('. ')[0];
  return firstSentence ? `${firstSentence.replace(/\.$/, '')}.` : `Study ${course.name} in ${destination.name} with the same ES World method and support.`;
}